import React, { Fragment } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';

import { TracksByCategory } from './types';
import { MUSIC_LIBRARY_VARIABLES } from './constants';
import AudioPlayer from './AudioPlayer';

import { useNonconcurrentPlay } from 'utils/audioPlayer/useNonconcurrentPlay';
import { ArrayElement, Normalize } from 'utils/types';
import { filterProps } from 'utils/helpers';

export type TMusicLibrary = ArrayElement<
  Normalize<Queries.MusicLibraryDataFragment['data']['content']>
>;

const Container = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    grid-template-columns: repeat(3, auto);
    row-gap: 3rem;
    column-gap: 6rem;
    align-self: flex-start;
    border-radius: 1rem;
    background-color: var(${MUSIC_LIBRARY_VARIABLES.trackContainerColor});
    padding: 3rem 6rem 6rem;
    width: 100%;

    &::before {
      display: block;
      content: '';
    }

    ${mq.md} {
      box-sizing: border-box;
      grid-template-columns: auto;
      padding: 2rem;

      &::before {
        display: none;
      }
    }
  `}
`;

const Name = styled('span', filterProps('isPlaceholder'))<{
  isPlaceholder?: boolean;
}>`
  ${({ theme: { mq, tp }, isPlaceholder = false }) => css`
    cursor: pointer;
    line-height: 1.14;
    letter-spacing: -0.04em;
    white-space: nowrap;
    color: var(${MUSIC_LIBRARY_VARIABLES.trackNameColor});
    font-size: 1.75rem;

    ${isPlaceholder
      ? css`
          height: 0;
          overflow-y: hidden;
          pointer-events: none;
        `
      : ''}

    ${tp.useFont(fonts => fonts.Switzer.regular)};

    ${mq.md} {
      display: none;
    }
  `}
`;

const Genres = styled(Name)`
  ${({ theme: { tp } }) => css`
    color: var(${MUSIC_LIBRARY_VARIABLES.trackGenresColor});

    ${tp.useFont(fonts => fonts.Switzer.light)};
  `}
`;

interface ITracks extends Omit<ReturnType<typeof useNonconcurrentPlay>, 'trackPlayerRefs'> {
  currentTracksCategory: TracksByCategory;
  largestTrackName: string;
  largestTrackGenres: string;
}

const Tracks: React.FC<ITracks> = ({
  currentTracksCategory,
  largestTrackName,
  largestTrackGenres,
  getTrackPlayerRef,
  composeOnPlay,
}) => (
  <Container>
    <Name isPlaceholder>{largestTrackName}</Name>
    <Genres isPlaceholder>{largestTrackGenres}</Genres>
    {currentTracksCategory.tracks.map(({ track, trackName, trackGenres }, currentIndex) => {
      const key = `${currentTracksCategory.name}${trackName}${trackGenres}${currentIndex}`;

      return (
        <Fragment key={key}>
          <AudioPlayer
            track={track}
            innerRef={getTrackPlayerRef(key)}
            onPlay={composeOnPlay(currentIndex)}
          />
          <Name>{trackName}</Name>
          <Genres>{trackGenres}</Genres>
        </Fragment>
      );
    })}
  </Container>
);

export const contentMusicLibraryTracksFragment = graphql`
  fragment MusicLibraryTracksData on MusicLibraryEnabledPage {
    ... on PrismicSolution {
      data {
        content {
          ... on PrismicSolutionDataContentMusicLibrary {
            items {
              music_library_category {
                document {
                  ... on PrismicMusicCategory {
                    ...MusicLibraryCategory
                  }
                }
              }
            }
          }
        }
      }
    }

    ... on PrismicIndustry {
      data {
        content {
          ... on PrismicIndustryDataContentMusicLibrary {
            items {
              music_library_category {
                document {
                  ... on PrismicMusicCategory {
                    ...MusicLibraryCategory
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment MusicLibraryCategory on PrismicMusicCategory {
    data {
      tracks {
        track_name
        track_genres
        track {
          url
        }
      }
    }
  }
`;

export default Tracks;
